/**
 * API fetch helper — calls conversations-service.
 * Routes that go through Vite proxy (/private) use relative URLs so cookies
 * are same-origin. All other routes use the backend base URL directly.
 *
 * Cross-origin requests (hub.tbit.app → api.tbit.app) include the accessToken
 * in the Authorization header because the host-only cookie isn't sent cross-origin.
 */
import { getAccessToken, notifySessionExpired, renewSession } from '~/lib/auth';

const API_BASE = import.meta.env.VITE_API_URL || '';

// These prefixes are proxied by Vite's dev server to conversations-service.
// In production there is no proxy, so we fall back to calling API_BASE directly
// (cross-origin) with a Bearer token instead.
const PROXIED_PREFIXES = ['/private/schedules', '/private/profile', '/private/health'];

export async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  const isProxyCandidate = PROXIED_PREFIXES.some((p) => path.startsWith(p));
  // Use the same-origin proxy only in dev (no API_BASE configured).
  const useProxy = isProxyCandidate && !API_BASE;
  const url = useProxy ? path : `${API_BASE}${path}`;
  const sendsBearer = !useProxy && !!API_BASE;

  const send = (token: string | null) => {
    const headers = new Headers(init?.headers);
    if (token && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return fetch(url, { ...init, headers, credentials: 'include' });
  };

  const staleToken = sendsBearer ? getAccessToken() : null;
  const response = await send(staleToken);

  // An expired access token is rejected before the route handler runs, so the
  // request had no effect and is safe to replay — including POSTs.
  if (response.status === 401 && sendsBearer) {
    const freshToken = await renewSession(staleToken);
    if (freshToken) return send(freshToken);
    // Nothing left to try: let the UI offer a way out rather than reporting
    // this as a failure of whatever the user was doing.
    notifySessionExpired();
  }

  return response;
}

export { API_BASE };
